import React, {useEffect, useRef, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Link,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Loading from "../../components/Loading";
import DidNotLoad from "../../components/DidNotLoad";
import api from "../../api";
import PhoneInput from "../../components/PhoneInput";
import useAlertModal from "../../components/AlertModal";
import { clearCachedFeatures } from "../../helpers/featureFlags";
import {Edit, Lock} from "@mui/icons-material";
import {formatCurrencyInCents, presentPhone, resolvePublicMediaUrl} from "../../helpers/formatters";
import { applyEmail, applyName, CAPTURE_AVATAR_SRC, isCaptureModeEnabled } from "../../helpers/captureMode";
import {validatePassword} from "../../helpers/validate_password";
import {hasPremiumAccess} from "../../helpers/subscription";
import Swal from "sweetalert2";

function MyAccountPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [whatsApp, setWhatsApp] = useState('');
    const initialData = {
        name: "",
        email: "",
        profile_picture: "",
        whatsapp: "",
        site: "",
        instagram: "",
        tiktok: "",
        store_name: "",
        address: "",
        address_number: "",
        address_complement: "",
        neighborhood: "",
        zip_code: "",
        city: "",
        state: "",
    };
    const [data, setData] = useState(initialData);
    const [plan, setPlan] = useState({
        name: 'Free',
        subscription_price: 0,
    });
    const [formData, setFormData] = useState(initialData);
    const [resetPasswordData, setResetPasswordData] = useState({
        current_password: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        has_errors: false,
        name: null,
        email: null,
        whatsapp: null,
        site: null,
        instagram: null,
        tiktok: null,
        store_name: null,
    });
    let responseReceived = false;
    const {showAlert} = useAlertModal();
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        clearCachedFeatures();
        console.log("Saindo...");
        window.location.href = '/admin/login';
    };

    // Fetch user data on load
    useEffect(() => {
            api.get("me/account")
                .then((response) => {
                    if (typeof response.data.data === "undefined") {
                        setDidNotLoad(true);
                    } else {
                        const receivedUser = response.data.data.user;
                        const receivedPlan = response.data.data.plan;
                        const receivedData = {
                            name: receivedUser.name,
                            email: receivedUser.email,
                            profile_picture: receivedUser.profile_picture,
                            whatsapp: receivedUser.whatsapp,
                            site: receivedUser.site,
                            instagram: receivedUser.instagram,
                            tiktok: receivedUser.tiktok,
                            store_name: receivedUser.store_name,
                            address: receivedUser.address || '',
                            address_number: receivedUser.address_number || '',
                            address_complement: receivedUser.address_complement || '',
                            neighborhood: receivedUser.neighborhood || '',
                            zip_code: receivedUser.zip_code || '',
                            city: receivedUser.city || '',
                            state: receivedUser.state || '',
                        }
                        setWhatsApp(receivedUser.whatsapp);
                        setData(receivedData);
                        setFormData(receivedData); // Set form data for editing
                        setPlan(receivedPlan);
                        try {
                            const stored = JSON.parse(localStorage.getItem('user') || '{}');
                            stored.plan_name = receivedPlan?.name || 'Free';
                            localStorage.setItem('user', JSON.stringify(stored));
                        } catch {
                            /* ignore */
                        }
                    }
                })
                .catch(() => {
                    setDidNotLoad(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
    }, []);

    // Handle form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle form changes
    const handleChangePasswordInput = (e) => {
        const { name, value } = e.target;
        setResetPasswordData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle form submission
    const handleSaveChanges = (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        const updatedFormData = { ...formData, whatsapp: whatsApp };
        api.patch("me", updatedFormData)
            .then((response) => {
                responseReceived = true;
                try {
                    const user = response.data.data;
                    const next = {
                        name: user.name,
                        email: user.email,
                        profile_picture: user.profile_picture,
                        whatsapp: user.whatsapp,
                        site: user.site,
                        instagram: user.instagram,
                        tiktok: user.tiktok,
                        store_name: user.store_name,
                        address: user.address || '',
                        address_number: user.address_number || '',
                        address_complement: user.address_complement || '',
                        neighborhood: user.neighborhood || '',
                        zip_code: user.zip_code || '',
                        city: user.city || '',
                        state: user.state || '',
                    };
                    setData(next);
                    setWhatsApp(user.whatsapp ?? '');

                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        let stored = JSON.parse(storedUser);

                        stored.name = user.name;
                        stored.email = user.email;
                        stored.profile_picture = user.profile_picture;
                        stored.whatsapp = user.whatsapp;
                        stored.site = user.site;
                        stored.instagram = user.instagram;
                        stored.tiktok = user.tiktok;
                        stored.store_name = user.store_name;

                        localStorage.setItem('user', JSON.stringify(stored));
                    }

                    setIsEditModalOpen(false);
                    showAlert({
                        title: "Sucesso!",
                        message: "Dados editados com sucesso.",
                        iconType: "success",
                        redirectTo: "/admin/my-account",
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((error) => {
                responseReceived = true;
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...(data.errors || {}),
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!responseReceived) {
                    setDidNotLoad(true);
                }
            });
    };

    // Handle form submission
    const handleChangePassword = (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        if (resetPasswordData.current_password === "") {
            setIsPasswordModalOpen(false);
            setIsSubmitting(false);
            Swal.fire({
                icon: "error",
                title: "Informe a senha atual",
                html: "Você precisa informar a senha atual para modificar.",
            });
            return;
        }

        const passwordValidationResult = validatePassword(resetPasswordData.new_password);
        const passwordsMatch = resetPasswordData.new_password === resetPasswordData.confirm_new_password
        if (!passwordValidationResult.isValid || !passwordsMatch) {
            setIsPasswordModalOpen(false);
            setIsSubmitting(false);
            let message = passwordValidationResult.message
            if (!passwordsMatch) {
                message = "As senhas não conferem";
            }
            Swal.fire({
                icon: "error",
                title: "Senha inválida",
                html: message, // Use `html` em vez de `text` para suportar quebras de linha
            });
            return
        }

        api.patch("me/password", {
            current_password: resetPasswordData.current_password,
            new_password: resetPasswordData.new_password,
        })
            .then((response) => {
                responseReceived = true;
                try {
                    const data = response.data.data;
                    if (data.updated) {
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('user');
                        clearCachedFeatures();
                        setIsPasswordModalOpen(false);
                        showAlert({
                            title: "Sucesso!",
                            message: "Senha alterada, você precisa realizar login novamente.",
                            iconType: "success",
                            redirectTo: '/admin/login',
                        });
                    } else {
                        setIsPasswordModalOpen(false);
                        showAlert({
                            title: "Erro!",
                            message: "Não foi possível atualizar sua senha.",
                            iconType: "error",
                        });
                    }
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((error) => {
                setIsPasswordModalOpen(false);
                responseReceived = true;
                try {
                    const data = error.response.data;
                    showAlert({
                        title: "Erro!",
                        message: data.message,
                        iconType: "error",
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsPasswordModalOpen(false);
                setIsSubmitting(false);
                if (!responseReceived) {
                    setDidNotLoad(true);
                }
            });
    };

    const handleEditProfilePicture = (event) => {
        const file = event.target.files[0];

        if (file) {
            // Validação de tipo e tamanho do arquivo
            if (file.size > 1 * 1024 * 1024) { // Máximo 2MB
                showAlert({
                    title: "Falha!",
                    message: "O arquivo não pode ser maior que 1MB.",
                    iconType: "error",
                    redirectTo: "/admin/my-account",
                });
                return;
            }
            if (!["image/jpeg", "image/png"].includes(file.type)) { // Apenas JPG e PNG
                showAlert({
                    title: "Falha!",
                    message: "Somente arquivos JPG ou PNG são permitidos.",
                    iconType: "error",
                    redirectTo: "/admin/my-account",
                });
                return;
            }

            // FileReader para converter em base64
            const reader = new FileReader();

            reader.onloadend = () => {
                // Converte a imagem em base64 e envia para o backend
                const base64Image = reader.result;

                // Simulação de envio
                console.log("Imagem Base64:", base64Image);

                // Aqui você pode realizar a lógica de upload (ex: API POST)
                api.put("me/avatar", { image: base64Image })
                    .then((response) => {
                        try {
                            const imageUrl = response.data.data.image_url;
                            setData((prev) => ({ ...prev, profile_picture: imageUrl }));
                            const storedUser = localStorage.getItem('user');
                            if (storedUser) {
                                let user = JSON.parse(storedUser);
                                user.profile_picture = imageUrl;
                                localStorage.setItem('user', JSON.stringify(user));
                            }
                        } catch (error) {
                            console.log(error);
                        }
                        responseReceived = true;
                        showAlert({
                            title: "Sucesso!",
                            message: "Dados editados com sucesso.",
                            iconType: "success",
                            redirectTo: "/admin/my-account",
                        });
                    })
                    .catch(() => {
                        responseReceived = true;
                        showAlert({
                            title: "Falha!",
                            message: "Não foi possível editar a imagem. Tente novamente em instantes.",
                            iconType: "error",
                            redirectTo: "/admin/my-account",
                        });
                    })
                    .finally(() => {
                        setIsSubmitting(false);
                        if (!responseReceived) {
                            setDidNotLoad(true);
                        }
                    });
            };

            reader.readAsDataURL(file); // Lê o arquivo como base64
        }
    };



    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <Box padding={2}>
            <Typography variant="h4" gutterBottom>
                Minha Conta
            </Typography>
            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3} sx={{ textAlign: "center", position: "relative" }}>
                        <Box sx={{ position: "relative", width: 120, height: 120, margin: "0 auto" }}>
                            {/* Avatar */}
                            <Avatar
                                src={isCaptureModeEnabled() ? CAPTURE_AVATAR_SRC : (resolvePublicMediaUrl(data.profile_picture) || "/default-avatar.png")}
                                alt={isCaptureModeEnabled() ? '' : data.name}
                                sx={{ width: 120, height: 120 }}
                            />
                            {/* Botão para abrir seletor de arquivo */}
                            <IconButton
                                sx={{
                                    position: "absolute",
                                    bottom: 5,
                                    right: 5,
                                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    color: "white",
                                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.9)" },
                                }}
                                component="label"
                            >
                                <Edit fontSize="small" />
                                {/* Input de arquivo escondido */}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    hidden
                                    onChange={handleEditProfilePicture} // Conecte ao handler
                                />
                            </IconButton>
                        </Box>
                    </Grid>


                    {/* User Details */}
                    <Grid item xs={12} sm={8} md={9}>
                        <Typography variant="h6">{applyName(data.name)}</Typography>
                        <Typography variant="body1" color="text.secondary" mb={1}>
                            {applyEmail(data.email)}
                        </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Loja: {data.store_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                WhatsApp: {presentPhone(data.whatsapp) || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Site: {data.site}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Instagram: {data.instagram}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                TikTok: {data.tiktok}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Endereço base: {[data.address, data.address_number, data.neighborhood, data.city, data.state].filter(Boolean).join(', ') || 'Não informado'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                {hasPremiumAccess(plan.name)
                                    ? `Plano: ${plan.name}${plan.subscription_price ? ` — ${formatCurrencyInCents(plan.subscription_price)}/mês` : ''}.`
                                    : `Plano: ${plan.name && plan.name !== 'Free' ? plan.name : 'Começando'}.`}
                            </Typography>
                    </Grid>
                </Grid>

                {/* Divider */}
                <Divider sx={{ marginY: 2 }} />

                {/* Actions */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        component={RouterLink}
                        to="/admin/my-account/subscription"
                    >
                        Minha assinatura
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsEditModalOpen(true)}
                        startIcon={<Edit />}
                    >
                        Editar informações
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsPasswordModalOpen(true)}
                        startIcon={<Lock />}
                    >
                        Alterar senha
                    </Button>
                    <Link
                        component={RouterLink}
                        to="/admin/tickets"
                        underline="hover"
                        color="primary"
                        sx={{ fontSize: "0.95rem" }}
                    >
                        Meus Tickets
                    </Link>
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        color="text.secondary"
                        onClick={handleLogout}
                        sx={{
                            fontSize: "0.95rem",
                            fontFamily: "inherit",
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                        }}
                    >
                        Sair da conta
                    </Link>
                </Box>
            </Paper>

            {/* Edit Info Modal */}
            <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} fullWidth>
                <DialogTitle>Editar Informações</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Nome"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                    />
                    {resultErrors.name && (
                        <div
                            className="text-danger mt-1"
                            style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                        >
                            {resultErrors.name}
                        </div>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                    {resultErrors.email && (
                        <div
                            className="text-danger mt-1"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            {resultErrors.email}
                        </div>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Nome da Loja"
                        name="store_name"
                        value={formData.store_name}
                        onChange={handleChange}
                    />
                    {resultErrors.store_name && (
                        <div
                            className="text-danger mt-1"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            {resultErrors.store_name}
                        </div>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Endereço base (mapa de parcerias)</Typography>
                    <TextField fullWidth margin="normal" label="Endereço" name="address" value={formData.address || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="Número" name="address_number" value={formData.address_number || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="Complemento" name="address_complement" value={formData.address_complement || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="Bairro" name="neighborhood" value={formData.neighborhood || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="CEP" name="zip_code" value={formData.zip_code || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="Cidade" name="city" value={formData.city || ''} onChange={handleChange} />
                    <TextField fullWidth margin="normal" label="UF" name="state" value={formData.state || ''} onChange={handleChange} />

                    <Box sx={{ marginTop: 2 }}></Box>

                    <PhoneInput
                        label="WhatsApp"
                        value={whatsApp}
                        onChange={(e) => setWhatsApp(e.target.value)}
                        disabled={false}
                    />
                    {resultErrors.whatsapp && (
                        <div
                            className="text-danger mt-1"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            {resultErrors.whatsapp}
                        </div>
                    )}

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Site"
                        name="site"
                        value={formData.site}
                        type="url"
                        onChange={handleChange}
                    />
                    {resultErrors.site && (
                        <div
                            className="text-danger mt-1"
                            style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                        >
                            {resultErrors.site}
                        </div>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Instagram"
                        name="instagram"
                        value={formData.instagram}
                        type="url"
                        onChange={handleChange}
                    />
                    {resultErrors.instagram && (
                        <div
                            className="text-danger mt-1"
                            style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                        >
                            {resultErrors.instagram}
                        </div>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        label="TikTok"
                        name="tiktok"
                        value={formData.tiktok}
                        type="url"
                        onChange={handleChange}
                    />
                    {resultErrors.tiktok && (
                        <div
                            className="text-danger mt-1"
                            style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                        >
                            {resultErrors.tiktok}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditModalOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveChanges}
                        color="primary"
                        variant="contained"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit password Modal */}
            <Dialog open={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} fullWidth>
                <DialogTitle>Alterar Senha</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Senha Atual"
                        name="current_password"
                        type="password"
                        onChange={handleChangePasswordInput}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Nova Senha"
                        name="new_password"
                        type="password"
                        onChange={handleChangePasswordInput}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Confirmar Nova Senha"
                        name="confirm_new_password"
                        type="password"
                        onChange={handleChangePasswordInput}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsPasswordModalOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleChangePassword}
                        color="primary"
                        variant="contained"
                    >
                        Alterar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MyAccountPage;
